/* 安全掲示ポスターのテーマ定義。
 *
 * docs/design-handoff/poster-prototype/themes.js の移植。
 * プロトタイプでは文字列に <br> / <em> / <b> を直接埋めていたが、
 * 本実装では掲示者名（会社名・現場名）と同じ経路で JSX 描画するため、
 * HTML 文字列を一切持たない構造に置き換えている（innerHTML を使わない＝注入経路を作らない）。
 *
 * 設計上の固定事項（プロトタイプからの引き継ぎ。理由があるので崩さない）:
 *  - 統計の数字は載せない（年次更新が運用の負債になる）
 *  - 下段（確認1行／直後の対処／QR見出し／根拠法令）はテーマごとに別物。
 *    熱中症「涼しい場所へ移す」／墜落「動かさない」／転倒「すぐ立たせない」は
 *    正反対の指示なので、共通化すると人を殺しうる。
 *  - 法令の条文番号は裏取りしたものだけを書く。
 */

/** 装飾つきテキストの断片。em = オレンジ強調、b = 本文中の太字。 */
export interface PosterSeg {
  t: string;
  em?: boolean;
  b?: boolean;
}

/** 1行分。改行位置は組版で意味を持つので、行の配列として明示的に持つ。 */
export type PosterLine = readonly PosterSeg[];

export type PosterThemeKey = 'heat' | 'fall' | 'caught' | 'trip' | 'general';

/* ── テーマの2系統 ─────────────────────────────────────────────
 * 'hazard'  災害別（熱中症・墜落・はさまれ・転倒）。絵/図解のヒーロー＋
 *           下段（症状／直後の対処／根拠法令）を持つ。
 * 'general' 汎用（画像なし）。全業種・通年で使う1枚。
 *           下段を持たない = 未確認の法令を業種横断で引用しないため。
 *
 * この2つは紙面の骨格が別物なので、共通の型に optional を生やして
 * 「あるはず」を実行時まで先送りせず、判別可能ユニオンで分ける。
 * theme.kind / variant.kind を見ないと下段のフィールドに触れない。
 * ───────────────────────────────────────────────────────────── */

/** 災害別テーマのヒーロー（標語＋絵）のバリアント。 */
export interface HazardPosterVariant {
  /** ユニオンの判別子。災害別は持たない（既存22種を書き換えないため undefined 側に置く）。 */
  kind?: undefined;
  /** 掲示物の識別子。URL（/posters/H1）にも出る。 */
  id: string;
  /** 想定業種・絵柄。ギャラリーのタグになる。 */
  use: string;
  /** 画像型のみ。/posters/*.webp（WebP 最適化済み。PNG は追加しない） */
  img?: string;
  /** object-position。横長画像を A4 の帯に収めるときの切り取り位置。 */
  pos?: string;
  /** 図解型。日本語の作図は画像生成では破綻するので SVG で組む。 */
  diagram?: 'heatSymptoms' | 'fallHeight' | 'caughtLoto' | 'tripTypes';
  /** 導入の一行（小さめ）。 */
  s1: string;
  /** 主標語。行ごとの配列。 */
  s2: readonly PosterLine[];
}

interface GeneralPosterVariantBase {
  kind: 'general';
  id: string;
  use: string;
  /** 導入の一行。最小レイアウトではリード文になる。 */
  s1: string;
  /** 主標語。最小レイアウトでは中央の一言になる。 */
  s2: readonly PosterLine[];
}

/** 余白重視の最小レイアウト。文例ブロックを持たず、QRを中央に大きく置く。 */
export interface GeneralMinimalVariant extends GeneralPosterVariantBase {
  minimal: true;
}

/** 標準レイアウト。差し替えるのは文例4つだけで、骨格・主張・QRは共通。 */
export interface GeneralExampleVariant extends GeneralPosterVariantBase {
  minimal?: false;
  /** 「こんなことで、かまいません」の文例。2×2で組むので必ず4つ。 */
  ex: readonly [string, string, string, string];
}

export type GeneralPosterVariant = GeneralMinimalVariant | GeneralExampleVariant;

export type PosterVariant = HazardPosterVariant | GeneralPosterVariant;

interface PosterThemeBase {
  key: PosterThemeKey;
  label: string;
  /** タブに出す一言。どの現場向けかを選ぶ手がかり。 */
  tagline: string;
}

export interface HazardPosterTheme extends PosterThemeBase {
  kind: 'hazard';
  check: { label: string; value: string };
  immediate: { title: string; accent: string; desc: string };
  qr: { h: readonly PosterLine[]; ex: readonly string[] };
  legal: PosterLine;
  variants: readonly HazardPosterVariant[];
}

export interface GeneralPosterTheme extends PosterThemeBase {
  kind: 'general';
  variants: readonly GeneralPosterVariant[];
}

export type PosterTheme = HazardPosterTheme | GeneralPosterTheme;

/** 素の文字列を 1 行に。 */
const t = (text: string): PosterLine => [{ t: text }];

/* 汎用テーマの見出しは全バリアント共通（差し替えるのは文例だけ、が設計）。
   1か所に置いて、業種違いで標語がずれることを型と実体の両方で防ぐ。 */
const GENERAL_S1 = '気づいた人だけが、知っています。';
const GENERAL_S2: readonly PosterLine[] = [
  t('その「あぶない」を、'),
  [{ t: '教えてください。', em: true }],
];

export const POSTER_THEMES: readonly PosterTheme[] = [
  {
    kind: 'hazard',
    key: 'heat',
    label: '熱中症',
    tagline: '夏季の屋外・高温環境',
    check: {
      label: 'この症状で作業中止',
      value: 'めまい／頭痛・吐き気／筋肉のけいれん／汗が出ない／呼びかけに反応が鈍い',
    },
    immediate: {
      title: '具合が悪い人がいたら',
      accent: 'すぐ人を呼ぶ',
      desc: '涼しい場所へ移し、首・わきの下・脚の付け根を冷やす。自力で水が飲めなければ、ためらわず救急要請。',
    },
    qr: {
      h: [t('倒れる前に、'), [{ t: '言ってください。', em: true }]],
      ex: ['ここ、風が通らなくて異常に暑い', 'さっき少しふらついた'],
    },
    legal: [
      { t: '熱中症のおそれがある作業では、発症者を発見した際の' },
      { t: '報告体制と連絡先を定め、関係作業者へ周知すること', b: true },
      {
        t: 'が事業者の義務とされています（労働安全衛生規則の一部を改正する省令／2025年6月1日施行）。詳細は厚生労働省「職場における熱中症予防基本対策要綱」および所轄労働基準監督署にご確認ください。',
      },
    ],
    variants: [
      {
        id: 'H1',
        use: '製造・建設（イラスト）',
        img: '/posters/b_care.webp',
        pos: 'center 58%',
        s1: 'その「大丈夫？」が、',
        s2: [t('いちばん早い'), [{ t: '熱中症対策', em: true }, { t: 'です。' }]],
      },
      {
        id: 'H2',
        use: '建設（昭和調）',
        img: '/posters/d_showa.webp',
        pos: 'center 38%',
        s1: '暑さに、根性は通じない。',
        s2: [t('無理をするな。'), [{ t: '休む勇気を。', em: true }]],
      },
      {
        id: 'H3',
        use: '保育・介護（水彩）',
        img: '/posters/f_water.webp',
        pos: 'center 50%',
        s1: 'ひとりで我慢させない。',
        s2: [t('気づいたら、'), [{ t: '声をかけて。', em: true }]],
      },
      {
        id: 'H4',
        use: '建設・屋外（写実）',
        img: '/posters/p_photo.webp',
        pos: 'center 45%',
        s1: '水を飲む時間は、作業時間です。',
        s2: [t('迷わず、'), [{ t: '手を止めて。', em: true }]],
      },
      {
        id: 'H5',
        use: '全業種（和・筆）',
        img: '/posters/w_sumi.webp',
        pos: 'center 50%',
        s1: 'この暑さは、',
        s2: [t('気合いでは'), [{ t: '下がらない。', em: true }]],
      },
      {
        id: 'H6',
        use: '物流・倉庫（イラスト）',
        img: '/posters/j_female.webp',
        pos: 'center 40%',
        s1: '言い出しにくさが、事故をつくる。',
        s2: [t('つらいと'), [{ t: '言える現場に。', em: true }]],
      },
      {
        id: 'H7',
        use: '全業種（図解）',
        diagram: 'heatSymptoms',
        s1: '体が出しているサインを、',
        s2: [t('見落とさない。')],
      },
    ],
  },

  {
    kind: 'hazard',
    key: 'fall',
    label: '墜落・転落',
    tagline: '高所作業・開口部・昇降設備',
    check: {
      label: '作業前に必ず確認',
      value: '作業床と手すり／開口部の囲い・覆い／フックの取付位置／器具のラベルと点検／昇降設備',
    },
    immediate: {
      title: '落ちた人がいたら',
      accent: '動かさず、すぐ人を呼ぶ',
      desc: '頭・首・背中を痛めている可能性がある。抱き起こさず救急要請。落下物による二次災害を止め、周囲の作業を停止させる。',
    },
    qr: {
      h: [t('「落ちそうだった」を、'), [{ t: '教えてください。', em: true }]],
      ex: ['この開口部、板がずれている', '掛ける場所がなくて外した'],
    },
    legal: [
      { t: '高さ2m以上の箇所での作業では、作業床の設置や囲い・手すり等の' },
      { t: '墜落防止措置', b: true },
      {
        t: 'が事業者に義務づけられています（労働安全衛生規則 第518条・第519条）。墜落制止用器具は2022年1月から新規格品のみ使用可能で、高さ6.75mを超える箇所では',
      },
      { t: 'フルハーネス型に限られます', b: true },
      { t: '。詳細は所轄労働基準監督署にご確認ください。' },
    ],
    variants: [
      {
        id: 'F1',
        use: '建設（イラスト）',
        img: '/posters/f1_harness_v2.webp',
        pos: 'center 42%',
        s1: '「掛ける場所がない」が、事故になる。',
        s2: [[{ t: 'フックは、' }, { t: '先に掛ける。', em: true }]],
      },
      {
        id: 'F2',
        use: '建設（写実）',
        img: '/posters/f2_opening.webp',
        pos: 'center 55%',
        s1: 'その板、固定されていますか。',
        s2: [t('開口部は、'), [{ t: '塞いだつもり', em: true }, { t: 'が危ない。' }]],
      },
      {
        id: 'F3',
        use: '建設・設備（昭和調）',
        img: '/posters/f3_ladder_v2.webp',
        pos: 'center 50%',
        s1: 'はしごの安全は、上る前に決まる。',
        s2: [t('固定と支えが、'), [{ t: '落ちない条件。', em: true }]],
      },
      {
        id: 'F4',
        use: '建設（開口部・フラット）',
        img: '/posters/f4_cover_v2.webp',
        pos: 'center 55%',
        s1: '覆うだけでは、まだ途中。',
        s2: [t('留めて、囲って、'), [{ t: 'はじめて安全。', em: true }]],
      },
      {
        id: 'F5',
        use: '全業種（図解）',
        diagram: 'fallHeight',
        s1: '高さで、やることが変わる。',
        s2: [[{ t: '2mから、' }, { t: '6.75mから。', em: true }]],
      },
    ],
  },

  {
    kind: 'hazard',
    key: 'caught',
    label: 'はさまれ・巻き込まれ',
    tagline: '機械・搬送・回転体',
    check: {
      label: '手を入れる前に',
      value: '運転停止／電源遮断と施錠／起動できないことの確認／ガード・覆いの復旧／単独作業を避ける',
    },
    immediate: {
      title: 'はさまれた人がいたら',
      accent: '機械を止めてから近づく',
      desc: 'まず非常停止と電源遮断。二次災害を防ぐため、止まる前に手を出さない。無理に引き抜かず救急要請する。',
    },
    qr: {
      h: [t('「ガードを外した」も、'), [{ t: '報告してください。', em: true }]],
      ex: ['この覆いが壊れたままになっている', '止めずに掃除している人がいる'],
    },
    legal: [
      { t: 'プレス機械には' },
      { t: '安全囲い・安全装置の設置', b: true },
      {
        t: 'が事業者に義務づけられています（労働安全衛生規則 第131条）。コンベヤ等では、原動機・プーリーの機能、非常停止装置の機能、回転軸・歯車・プーリー等の覆いの異常について',
      },
      { t: '始業前点検', b: true },
      { t: 'が義務づけられています（同規則 第151条の79）。詳細は所轄労働基準監督署にご確認ください。' },
    ],
    variants: [
      {
        id: 'C1',
        use: '製造（イラスト）',
        img: '/posters/h1_loto_v2.webp',
        pos: 'center 45%',
        s1: '「すぐ終わる」で、手を入れない。',
        s2: [[{ t: '止めて、' }, { t: '電源を切って、施錠。', em: true }]],
      },
      {
        id: 'C2',
        use: '製造（写実）',
        img: '/posters/h2_nip.webp',
        pos: 'center 50%',
        s1: '動いている機械に、手は勝てない。',
        s2: [t('ガードを開けたら、'), [{ t: 'まず止める。', em: true }]],
      },
      {
        id: 'C3',
        use: '製造（劇画調）',
        img: '/posters/c3_stop_v2.webp',
        pos: 'center 50%',
        s1: '危ないと思ったら、止めていい。',
        s2: [t('その一言が、'), [{ t: '手を守る。', em: true }]],
      },
      {
        id: 'C4',
        use: '製造・食品（フラット）',
        img: '/posters/c4_guard_v2.webp',
        pos: 'center 62%',
        s1: '外したガードは、必ず戻す。',
        s2: [t('戻したかを、'), [{ t: '二人で見る。', em: true }]],
      },
      {
        id: 'C5',
        use: '全業種（図解）',
        diagram: 'caughtLoto',
        s1: '手を入れる前の、四つの手順。',
        s2: [[{ t: '止める・切る・' }, { t: '施錠・確認。', em: true }]],
      },
    ],
  },

  {
    kind: 'hazard',
    key: 'trip',
    label: '転倒',
    tagline: '通路・床面・段差',
    check: {
      label: '見つけたら、その場で直す',
      value: '床の水・油／通路の段差／置かれた台車・段ボール／垂れたコード／暗い足元',
    },
    immediate: {
      title: '転んだ人がいたら',
      accent: 'すぐに立たせない',
      desc: '頭を打っていないか、どこが痛むかを本人に確認する。骨折や頭部打撲の疑いがあれば動かさず救急要請。原因になった水や障害物をその場で除去する。',
    },
    qr: {
      h: [t('「滑りそう」の段階で、'), [{ t: '報告してください。', em: true }]],
      ex: ['ここ、いつも水が溜まっている', '台車が通路に出たままになっている'],
    },
    legal: [
      { t: '通路は安全に通行できる状態に保つ必要があります。厚生労働省「STOP！転倒災害プロジェクト」では、' },
      { t: '床面の水・油の除去、通路の障害物の排除、足元の照度確保', b: true },
      { t: 'が重点対策として示されています。詳細は所轄労働基準監督署にご確認ください。' },
    ],
    variants: [
      {
        id: 'T1',
        use: '物流・小売（イラスト）',
        img: '/posters/t1_trip.webp',
        pos: 'center 45%',
        s1: '荷物を持つと、足元は見えない。',
        s2: [t('通路に置いた'), [{ t: '「ちょっと」', em: true }, { t: 'が転ばせる。' }]],
      },
      {
        id: 'T2',
        use: '厨房・バックヤード（写実）',
        img: '/posters/t2_wetfloor.webp',
        pos: 'center 55%',
        s1: '濡れた床は、気をつけても滑る。',
        s2: [t('気づいた人が、'), [{ t: 'その場で拭く。', em: true }]],
      },
      {
        id: 'T3',
        use: '物流・倉庫（劇画調）',
        img: '/posters/t3_stumble.webp',
        pos: 'center 50%',
        s1: '急いでいる人は、足元を見ない。',
        s2: [t('だから、通路には'), [{ t: '置かない。', em: true }]],
      },
      {
        id: 'T4',
        use: '介護・医療（水彩）',
        img: '/posters/t4_care.webp',
        pos: 'center 60%',
        s1: '拭き終わるまでが、いちばん危ない。',
        s2: [t('先に置く、'), [{ t: '「濡れています」。', em: true }]],
      },
      {
        id: 'T5',
        use: '全業種（図解）',
        diagram: 'tripTypes',
        s1: '転倒は、三つの形で起きる。',
        s2: [[{ t: 'つまずく・' }, { t: '滑る・踏み外す。', em: true }]],
      },
    ],
  },

  /* 汎用（画像なし）。docs/design-handoff/poster-prototype/general.html の移植。
   *
   * 全業種・通年で1枚。差し替えるのは文例だけで、骨格・主張・QRは共通。
   * 画像を持たないので生成コストゼロ、白黒印刷でも成立する。
   *
   * 報告が上がらない障壁は2つあり、紙面はその2つを潰すためだけに組んである:
   *   ①「怒られるかも」        → 匿名の約束（濃紺ブロック）を紙面の中心に置く
   *   ②「こんな小さいことを」  → あえて小さい文例を並べ、報告の基準を下げる
   * 法令は載せない（業種横断のため、未確認の根拠を引用しない）。
   * 代わりにフッターで「集めた情報をどう扱うか」を約束する。 */
  {
    kind: 'general',
    key: 'general',
    label: '汎用（画像なし）',
    tagline: '全業種・通年／白黒印刷向き',
    variants: [
      {
        kind: 'general',
        id: 'G0',
        use: '最小限（余白重視）',
        minimal: true,
        s1: '安全のことで気づいたことがあれば',
        s2: [[{ t: 'ここから、' }, { t: '知らせてください。', em: true }]],
      },
      {
        kind: 'general',
        id: 'G1',
        use: '建設',
        s1: GENERAL_S1,
        s2: GENERAL_S2,
        ex: [
          '開口部の板がずれている',
          '掛ける場所がなくてフックを外した',
          '通路に資材が置かれたままになっている',
          '足場の一部が濡れて滑りやすい',
        ],
      },
      {
        kind: 'general',
        id: 'G2',
        use: '製造',
        s1: GENERAL_S1,
        s2: GENERAL_S2,
        ex: [
          'この覆いが壊れたままになっている',
          '止めずに掃除している人がいる',
          '床に油が溜まっていて滑りそう',
          '非常停止ボタンの前に物が置いてある',
        ],
      },
      {
        kind: 'general',
        id: 'G3',
        use: '物流・小売',
        s1: GENERAL_S1,
        s2: GENERAL_S2,
        ex: [
          '台車が通路に出たままになっている',
          'バックヤードの床がいつも濡れている',
          '棚の上の箱が落ちてきそう',
          'フォークリフトの死角で人とすれ違う',
        ],
      },
      {
        kind: 'general',
        id: 'G4',
        use: 'オフィス・全業種',
        s1: GENERAL_S1,
        s2: GENERAL_S2,
        ex: [
          'コードが足元を横切っている',
          '非常口の前に物が置かれている',
          '暗くて階段の足元が見えない',
          '脚立を使わず椅子に乗って作業していた',
        ],
      },
    ],
  },
];

/** 全バリアントを平坦に引く（プレビューの deep link 解決用）。 */
export const POSTER_INDEX: ReadonlyArray<{ theme: PosterTheme; variant: PosterVariant }> =
  POSTER_THEMES.flatMap((theme) => theme.variants.map((variant) => ({ theme, variant })));

export function findPoster(id: string | null | undefined) {
  if (!id) return null;
  const upper = id.toUpperCase();
  return POSTER_INDEX.find((p) => p.variant.id === upper) ?? null;
}

export const POSTER_TOTAL = POSTER_INDEX.length;
